/**
 * VZ REWIND — interaction layer for the Safari-2003 storefront templates.
 *
 * Every control here is a progressive enhancement of something that already
 * works without JS:
 *   Back / Home / bookmarks / pills / tabs  -> real <a href>
 *   Search                                  -> real GET form to /search
 *   Filters + sort                          -> real GET form to the collection
 *   Add to cart / quick add                 -> real POST to /cart/add
 *   Cart button                             -> real link to /cart
 * The only JS-only control is Reload, which re-fetches the current section via
 * the Section Rendering API. It is hidden on mobile and never claims state it
 * has not read back from the store.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '.vzr-root';
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function roots() {
    return Array.prototype.slice.call(document.querySelectorAll(ROOT_SELECTOR));
  }

  function each(list, fn) {
    Array.prototype.forEach.call(list, fn);
  }

  /* ------------------------------------------------------------------ *
   * Status bar — real store state only, never invented telemetry.
   * ------------------------------------------------------------------ */
  var statusTimer = null;

  function setStatus(msg, sticky) {
    var els = document.querySelectorAll('[data-vzr-status]');
    if (!els.length) return;
    each(els, function (el) {
      if (!el.dataset.vzrStatusDefault) el.dataset.vzrStatusDefault = el.textContent.trim();
      el.textContent = msg;
    });
    window.clearTimeout(statusTimer);
    if (!sticky) {
      statusTimer = window.setTimeout(function () {
        each(els, function (el) { el.textContent = el.dataset.vzrStatusDefault || ''; });
      }, 3600);
    }
  }

  /* ------------------------------------------------------------------ *
   * Menu-strip clock — the viewer's own local time, filled by JS only.
   * ------------------------------------------------------------------ */
  function startClock() {
    var els = document.querySelectorAll('[data-vzr-clock]');
    if (!els.length) return;
    var days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    function tick() {
      var d = new Date();
      var h = d.getHours();
      var m = d.getMinutes();
      var ap = h >= 12 ? 'PM' : 'AM';
      h = h % 12 || 12;
      var text = days[d.getDay()] + ' ' + h + ':' + (m < 10 ? '0' : '') + m + ' ' + ap;
      each(els, function (el) { el.textContent = text; });
    }

    tick();
    window.setInterval(tick, 30000);
  }

  /* ------------------------------------------------------------------ *
   * Address-field load fill — progress paints inside the URL capsule.
   * ------------------------------------------------------------------ */
  function runFill() {
    if (reducedMotion.matches) return;
    each(document.querySelectorAll('[data-vzr-fill]'), function (fill) {
      fill.style.transition = 'none';
      fill.style.right = '100%';
      fill.style.opacity = '';
      window.requestAnimationFrame(function () {
        fill.style.transition = '';
        fill.style.right = '0%';
        window.setTimeout(function () {
          fill.style.transition = 'opacity 260ms ease';
          fill.style.opacity = '0';
          window.setTimeout(function () {
            fill.style.right = '100%';
            fill.style.opacity = '';
            fill.style.transition = 'none';
          }, 280);
        }, 620);
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * Cart: read the real cart, never a local tally.
   * ------------------------------------------------------------------ */
  function formatMoney(cents) {
    var fmt = (window.theme && theme.settings && theme.settings.moneyWithCurrencyFormat) || '';
    var amount = (cents / 100).toFixed(2);
    // Symmetry ships a currency format string; fall back to a plain amount.
    var m = fmt.match(/\{\{\s*amount[^}]*\}\}/);
    if (m) return fmt.replace(m[0], amount).replace(/<[^>]+>/g, '').trim();
    return '$' + amount;
  }

  function refreshCart() {
    return fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'cart.js'
      : '/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        each(document.querySelectorAll('[data-vzr-cart-count]'), function (el) {
          el.textContent = cart.item_count;
        });
        each(document.querySelectorAll('[data-vzr-cart]'), function (el) {
          el.setAttribute('aria-label', 'Open cart, ' + cart.item_count
            + (cart.item_count === 1 ? ' item' : ' items'));
        });
        each(document.querySelectorAll('[data-vzr-cart-summary]'), function (el) {
          el.textContent = 'Bag: ' + cart.item_count
            + (cart.item_count === 1 ? ' item' : ' items')
            + ' · ' + formatMoney(cart.total_price);
        });
        return cart;
      })
      .catch(function () { /* leave the server-rendered figures in place */ });
  }

  /* Bag button: upgrade the /cart link into the theme's drawer when there is one. */
  function bindCartButton() {
    document.addEventListener('click', function (evt) {
      var btn = evt.target.closest && evt.target.closest('[data-vzr-cart]');
      if (!btn) return;
      var drawer = document.querySelector('cart-drawer');
      var wantsDrawer = window.theme && theme.settings && theme.settings.cartType === 'drawer';
      if (!drawer || !wantsDrawer || evt.metaKey || evt.ctrlKey) return;
      evt.preventDefault();
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: btn } }));
    });
  }

  /* ------------------------------------------------------------------ *
   * Reload — re-fetch this section through the Section Rendering API.
   * ------------------------------------------------------------------ */
  function bindReload() {
    document.addEventListener('click', function (evt) {
      var btn = evt.target.closest && evt.target.closest('[data-vzr-reload]');
      if (!btn) return;
      var root = btn.closest(ROOT_SELECTOR);
      var sectionId = root && root.dataset.vzrSection;
      var viewport = root && root.querySelector('[data-vzr-viewport]');
      if (!sectionId || !viewport) return;

      evt.preventDefault();
      if (btn.getAttribute('aria-busy') === 'true') return;
      btn.setAttribute('aria-busy', 'true');

      var icon = btn.querySelector('[data-vzr-reload-icon]');
      if (icon && icon.animate && !reducedMotion.matches) {
        icon.animate(
          [{ transform: 'rotate(0deg)' }, { transform: 'rotate(360deg)' }],
          { duration: 520, easing: 'ease-in-out' }
        );
      }
      setStatus('Refreshing product availability…', true);
      runFill();

      var params = new URLSearchParams(window.location.search);
      params.set('section_id', sectionId);
      fetch(window.location.pathname + '?' + params.toString())
        .then(function (r) {
          if (!r.ok) throw new Error(r.status);
          return r.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-vzr-viewport]');
          if (!fresh) throw new Error('no viewport in section response');
          viewport.innerHTML = fresh.innerHTML;
          var freshStatus = doc.querySelector('[data-vzr-status]');
          setStatus(freshStatus ? freshStatus.textContent.trim() : 'Availability updated', false);
          initGalleries();
          initLightboxes();
        })
        .catch(function () {
          setStatus('Could not refresh just now — showing the last loaded data', false);
        })
        .then(function () {
          btn.removeAttribute('aria-busy');
        });
    });
  }

  /* ------------------------------------------------------------------ *
   * Collection rail — auto-submit the real filter/sort GET form.
   * The form keeps a visible Apply button, so this is enhancement only.
   * ------------------------------------------------------------------ */
  function bindRail() {
    document.addEventListener('change', function (evt) {
      var form = evt.target.closest && evt.target.closest('[data-vzr-rail-form]');
      if (!form) return;
      setStatus('Applying filters…', true);
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.submit();
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * PDP gallery — thumbnails, plus variant-linked media switching.
   * ------------------------------------------------------------------ */
  function showMedia(gallery, mediaId) {
    var target = gallery.querySelector('.vzr-media[data-media-id="' + mediaId + '"]');
    if (!target) return false;
    each(gallery.querySelectorAll('.vzr-media'), function (el) {
      el.removeAttribute('data-vzr-active');
    });
    target.setAttribute('data-vzr-active', '');
    var thumbs = gallery.parentNode.querySelectorAll('[data-vzr-thumb]');
    each(thumbs, function (btn) {
      btn.setAttribute('aria-current', String(btn.dataset.vzrThumb === String(mediaId)));
    });
    return true;
  }

  function initGalleries() {
    each(document.querySelectorAll('[data-vzr-gallery]'), function (wrap) {
      var main = wrap.querySelector('[data-vzr-gallery-main]');
      if (!main || main.dataset.vzrBound) return;
      main.dataset.vzrBound = '1';

      wrap.addEventListener('click', function (evt) {
        var btn = evt.target.closest && evt.target.closest('[data-vzr-thumb]');
        if (!btn) return;
        evt.preventDefault();
        showMedia(main, btn.dataset.vzrThumb);
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * PDP lightbox — the main shot is a real link to the full-size image;
   * with JS it opens in a <dialog> with prev/next, arrow keys and swipe.
   * Stepping in the lightbox keeps the page gallery on the same shot.
   * ------------------------------------------------------------------ */
  function initLightboxes() {
    each(document.querySelectorAll('[data-vzr-gallery]'), function (wrap) {
      var dlg = wrap.querySelector('[data-vzr-lightbox]');
      var main = wrap.querySelector('[data-vzr-gallery-main]');
      if (!dlg || !main || typeof dlg.showModal !== 'function' || dlg.dataset.vzrBound) return;
      dlg.dataset.vzrBound = '1';

      var links = main.querySelectorAll('[data-vzr-zoom]');
      var img = dlg.querySelector('[data-vzr-lb-img]');
      var count = dlg.querySelector('[data-vzr-lb-count]');
      var index = 0;
      var touchX = null;

      function show(i) {
        index = (i + links.length) % links.length;
        var link = links[index];
        var thumb = link.querySelector('img');
        img.src = link.getAttribute('href');
        img.alt = thumb ? thumb.alt : '';
        if (count) count.textContent = (index + 1) + ' / ' + links.length;
        var media = link.closest('[data-media-id]');
        if (media) showMedia(main, media.dataset.mediaId);
      }

      main.addEventListener('click', function (evt) {
        var link = evt.target.closest && evt.target.closest('[data-vzr-zoom]');
        if (!link || evt.metaKey || evt.ctrlKey || evt.shiftKey) return;
        evt.preventDefault();
        show(parseInt(link.dataset.vzrZoom, 10) || 0);
        dlg.showModal();
      });

      dlg.addEventListener('click', function (evt) {
        var step = evt.target.closest('[data-vzr-lb-step]');
        if (step) { show(index + parseInt(step.dataset.vzrLbStep, 10)); return; }
        // close on the X, or on the backdrop / empty stage around the image
        if (evt.target.closest('[data-vzr-lb-close]') || evt.target === dlg ||
            evt.target.classList.contains('vzr-lightbox__stage')) {
          dlg.close();
        }
      });

      dlg.addEventListener('keydown', function (evt) {
        if (links.length < 2) return;
        if (evt.key === 'ArrowRight') { evt.preventDefault(); show(index + 1); }
        if (evt.key === 'ArrowLeft') { evt.preventDefault(); show(index - 1); }
      });

      dlg.addEventListener('touchstart', function (evt) {
        touchX = evt.touches.length === 1 ? evt.touches[0].clientX : null;
      }, { passive: true });
      dlg.addEventListener('touchend', function (evt) {
        if (touchX === null || links.length < 2) return;
        var dx = evt.changedTouches[0].clientX - touchX;
        touchX = null;
        if (Math.abs(dx) > 45) show(index + (dx < 0 ? 1 : -1));
      });

      dlg.addEventListener('close', function () {
        var active = main.querySelector('[data-vzr-active] [data-vzr-zoom]');
        if (active) active.focus({ preventScroll: true });
      });
    });
  }

  /* Variant change -> swap the main shot to the variant's own media. */
  function bindVariantMedia() {
    document.addEventListener('on:variant:change', function (evt) {
      var variant = evt.detail && evt.detail.variant;
      var root = evt.target.closest ? evt.target.closest(ROOT_SELECTOR) : null;
      if (!root) return;

      var gallery = root.querySelector('[data-vzr-gallery-main]');
      if (gallery && variant && variant.featured_media && variant.featured_media.id) {
        showMedia(gallery, variant.featured_media.id);
      }

      var stock = root.querySelector('[data-vzr-stock]');
      if (stock) {
        var available = !!(variant && variant.available);
        var exists = !!variant;
        stock.classList.toggle('vzr-stock--out', !available);
        stock.querySelector('[data-vzr-stock-text]').textContent = !exists
          ? stock.dataset.vzrNoVariant
          : (available ? stock.dataset.vzrInStock : stock.dataset.vzrSoldOut);
      }

      if (variant) {
        setStatus(variant.title + ' — ' + (variant.available ? 'in stock' : 'sold out'), false);
      }
    });
  }

  /* ------------------------------------------------------------------ *
   * Cart events raised by the theme's own product-form component.
   * ------------------------------------------------------------------ */
  function bindCartEvents() {
    document.addEventListener('on:cart:add', function () {
      setStatus('Added to your bag', false);
      refreshCart();
    });
    document.addEventListener('on:cart:change', refreshCart);
    document.addEventListener('on:cart:error', function () {
      setStatus('That combination could not be added — please try again', false);
    });
  }

  /* ------------------------------------------------------------------ *
   * Quick add on the collection grid: keep the real POST, but do it in the
   * background so the shopper is not thrown off the grid.
   * ------------------------------------------------------------------ */
  function bindQuickAdd() {
    document.addEventListener('submit', function (evt) {
      var form = evt.target;
      if (!form.matches || !form.matches('[data-vzr-quick-add]')) return;
      if (!window.fetch) return; // native POST still works

      evt.preventDefault();
      var btn = form.querySelector('[type="submit"]');
      if (btn && btn.getAttribute('aria-disabled') === 'true') return;
      if (btn) btn.setAttribute('aria-disabled', 'true');
      setStatus('Adding to bag…', true);

      fetch((window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form)
      })
        .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
        .then(function (res) {
          if (!res.ok) {
            setStatus(res.data.description || res.data.message || 'Could not add that item', false);
            return;
          }
          form.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId: res.data.variant_id } }));
          form.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
        })
        .catch(function () {
          setStatus('Could not add that item — please try again', false);
        })
        .then(function () {
          if (btn) btn.removeAttribute('aria-disabled');
        });
    });
  }

  /* ------------------------------------------------------------------ */
  function init() {
    if (!roots().length) return;
    startClock();
    runFill();
    initGalleries();
    initLightboxes();
    bindReload();
    bindRail();
    bindCartButton();
    bindCartEvents();
    bindVariantMedia();
    bindQuickAdd();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
